import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Sparkles } from 'lucide-react';
import { NAV_CATEGORIES, CATEGORY_META, SERVICE_CATALOG, getServiceGuide } from '../mock';
import { useLanguage } from '../i18n/LanguageContext';
import { trackPageLifecycle } from '../lib/customerEventTracker';
import { serviceTitle, serviceDesc, serviceStartingPrice } from '../i18n/serviceCopy';

const Services = () => {
  const { t } = useLanguage();
  const { slug } = useParams();
  const navigate = useNavigate();
  const upper = (slug || '').toUpperCase();
  const category = NAV_CATEGORIES.find((c) => c.category === upper);

  useEffect(() => {
    if (!category) return undefined;
    return trackPageLifecycle({
      funnel_name: 'SERVICES_CATEGORY',
      form_name: `Services_${category.category}`,
      service_code: null,
      open_event_name: 'services_category_open',
    });
  }, [category]);

  // Fallback: if user lands on /services/<service-code>, treat as service link
  if (!category) {
    const svc = SERVICE_CATALOG.find((s) => s.code === upper);
    if (svc) {
      const target = svc.category === 'INCOME_TAX' ? `/file-itr?service=${svc.code}` : `/get-started?service=${svc.code}`;
      navigate(target, { replace: true });
      return null;
    }
  }

  const meta = category ? CATEGORY_META[category.category] : null;
  const catTitle = meta ? t(`category.${category.category}`, meta.label) : '';
  const catDesc = meta ? t(`category.${category.category}_desc`, meta.desc) : '';
  const categoryServicesWithGuide = category
    ? category.services.map((svc) => ({ svc, guide: getServiceGuide(svc.code) }))
    : [];

  if (!category) {
    return (
      <div className="pt-28 pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-3xl font-extrabold text-[#0f2540] mb-4">{t('services.not_found', 'Service not found')}</h1>
          <p className="text-gray-600 mb-6">{t('services.browse_home', 'Browse all our services on the home page.')}</p>
          <Link to="/"><Button className="bg-[#14A098] hover:bg-[#0e7a74] text-white rounded-full">{t('services.back_home', 'Back to Home')}</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28">
      <section className="py-10 hero-gradient">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#14A098]/20 mb-4">
            <Sparkles className="w-4 h-4 text-[#14A098]" />
            <span className="text-xs font-semibold text-[#0f2540]">{catTitle}</span>
          </div>
          <h1 className="ui-title-xl mb-3">{catTitle} {t('services.services_suffix', 'Services')}</h1>
          <p className="ui-body max-w-3xl mx-auto">{catDesc}</p>
        </div>
      </section>

      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {categoryServicesWithGuide.map(({ svc, guide }) => {
            const href = svc.category === 'INCOME_TAX' ? `/file-itr?service=${svc.code}` : `/get-started?service=${svc.code}`;
            return (
              <Link key={svc.code} to={href} className="card-lift group p-4 rounded-2xl border border-gray-100 bg-white">
                <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center mb-3 group-hover:bg-gradient-to-br group-hover:from-[#14A098] group-hover:to-[#5eead4] transition-colors">
                  <svc.icon className="w-5 h-5 text-[#14A098] group-hover:text-white" />
                </div>
                <h3 className="font-bold text-[#0f2540] mb-1.5 leading-snug">{serviceTitle(svc.code, t, svc.title)}</h3>
                <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">{serviceDesc(svc.code, t, svc.desc)}</p>
                <div className="mt-3 rounded-xl border border-[#14A098]/25 bg-white px-3 py-2 shadow-sm">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#14A098]">{t('services.starting_price', 'Starting price')}</p>
                  <p className="text-base font-extrabold text-[#0f2540] tabular-nums leading-tight mt-0.5">
                    {serviceStartingPrice(svc.code, t, guide?.startingPrice)}
                  </p>
                </div>
                <div className="mt-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#14A098] bg-[#14A098] px-3 py-1.5 text-xs font-semibold text-white shadow-sm whitespace-normal text-center leading-snug">
                    {t('common.get_my_service_done', 'Get my {service} done', { service: serviceTitle(svc.code, t, svc.title) })}{' '}
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="py-14 bg-teal-50/30">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-[#0f2540] mb-3">{t('services.need_help_title', 'Need help choosing?')}</h2>
          <p className="text-gray-600 mb-6">{t('services.need_help_body', 'Our experts can recommend the right combination of services for your business.')}</p>
          <div className="flex flex-wrap gap-4 justify-center items-stretch">
            <Link to="/get-started" className="inline-flex">
              <Button
                size="lg"
                className="relative overflow-hidden rounded-full border-2 border-white/40 bg-gradient-to-r from-[#0d8a84] via-[#14A098] to-[#2dd4bf] px-5 sm:px-8 py-4 min-h-12 h-auto text-white font-extrabold text-sm sm:text-base shadow-[0_10px_40px_rgba(20,160,152,0.45),0_0_0_1px_rgba(255,255,255,0.15)_inset] ring-2 ring-[#14A098]/30 ring-offset-2 ring-offset-teal-50/30 transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_14px_48px_rgba(20,160,152,0.55)] hover:brightness-105 active:scale-[0.99] whitespace-normal text-center leading-snug max-w-[20rem] sm:max-w-[24rem]"
              >
                <span className="relative z-10 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-2.5">
                  <span className="inline-flex items-center gap-2">
                    <Sparkles className="w-5 h-5 sm:w-5 sm:h-5 text-amber-200 drop-shadow shrink-0" aria-hidden />
                    <span>{t('cta.multi_service', 'You can request multiple services')}</span>
                  </span>
                  <ArrowRight className="w-5 h-5 shrink-0 opacity-95 sm:ml-1" aria-hidden />
                </span>
                <span className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" aria-hidden />
              </Button>
            </Link>
            <Link to="/contact" className="inline-flex items-center">
              <Button size="lg" variant="outline" className="border-[#14A098] text-[#14A098] rounded-full self-center">{t('hero.cta_talk', 'Talk to an Expert')}</Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
